import { useEffect, useState } from 'react';
import {
  DEFAULT_CLOCK_IN_DATA,
  DEFAULT_COMPLETED_FOREST_DATA,
  DEFAULT_EXPENSE_CLAIM_DATA,
  DEFAULT_HARVESTING_CLOSING_DATA,
  DEFAULT_HARVESTING_MAINTENANCE_CHECK_LIST,
  DEFAULT_HOLIDAY_DATA,
  DEFAULT_OTHER_WORK_DATA,
  DEFAULT_STOCK_TAKE_DATA,
  DEFAULT_VEHICAL_CHECK_LIST,
  FORWARDING_DEFAULT_DATA,
  FORWARDING_MAINTENANCE_CHECK_LIST,
} from '../Data/DefaultHFTaskData';
import { useDispatch } from 'react-redux';
import customAxios, { customFormAxios } from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '@jumbo/constants/ActionTypes';
import { useToast } from 'context/tostifyContext';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';

// ASSUMPTION: pull this from HFDropDowdData if it already exists there.
export const HF_TASK_TYPE_OPTIONS = [
  'Clock In',
  'Daily Harvesting Close',
  'Daily Forwarding Close',
  'Daily Other Work Close',
  'Completed Forest',
  'Stock Take',
  'Expense Claim',
  'Other',
  'Holidays',
  'Help Videos',
];

const useHFOpsFormAddHook = (open, setOpen, handleSuccessfulSubmit) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const [spList, setSPList] = useState([]);
  const [spListObj, setSPListObj] = useState({});

  const [machineList, setMachineList] = useState([]);

  const [selectedTaskType, setSelectedTaskType] = useState(null);

  // ── form data slices (same shape as edit hook) ──────────────────────
  const [clockInData, setClockInData] = useState(DEFAULT_CLOCK_IN_DATA);
  const [vehicalCheckList, setVehicalChecklist] = useState(DEFAULT_VEHICAL_CHECK_LIST);

  const [harvestingMaintenanceCheckList, setHarvestingMaintenanceCheckList] = useState(
    DEFAULT_HARVESTING_MAINTENANCE_CHECK_LIST,
  );
  const [harvestingClosingData, setHarvestingClosingData] = useState(DEFAULT_HARVESTING_CLOSING_DATA);
  const [productionData, setProductionData] = useState([]);

  const [forwardingData, setForwardingData] = useState(FORWARDING_DEFAULT_DATA);
  const [forwardingMaintenanceCheckListData, setForwardingMaintenanceCheckListData] = useState(
    FORWARDING_MAINTENANCE_CHECK_LIST,
  );

  const [dailyOtherWorkCloseData, setDailyOtherWorkCloseData] = useState({});
  const [completedForestData, setCompletedForestData] = useState(DEFAULT_COMPLETED_FOREST_DATA);
  const [stockTakeData, setStockTakeData] = useState(DEFAULT_STOCK_TAKE_DATA);
  const [expenseClaimData, setExpenseClaimData] = useState(DEFAULT_EXPENSE_CLAIM_DATA);
  const [otherTaskData, setOtherTaskData] = useState(DEFAULT_OTHER_WORK_DATA);
  const [holidayData, setHolidayData] = useState(DEFAULT_HOLIDAY_DATA);

  // ── local file staging (no server-backed docs yet pre-submit) ──────
  const [files, setFiles] = useState([]); // display list: [{ id, documentName, fileObj }]
  const [newUploadedFiles, setNewUploadedFiles] = useState([]); // raw File[] for upload

  useEffect(() => {
    getSPList();
    getMachineList();
  }, []);

  // Reset everything whenever the dialog opens fresh
  useEffect(() => {
    if (open) {
      resetAllData();
    }
  }, [open]);

  useEffect(() => {
    if (!productionData) return;

    const total = productionData.reduce((acc, item) => {
      const val = parseFloat(item?.m3 || '0');
      return acc + (isNaN(val) ? 0 : val);
    }, 0);

    setHarvestingClosingData(prev => ({
      ...prev,
      total_M3: total.toFixed(3),
    }));

    setForwardingData(prev => ({
      ...prev,
      total_M3: total.toFixed(3),
    }));
  }, [productionData]);

  const getSPList = async () => {
    try {
      dispatch({ type: FETCH_START });
      customAxios.get('/Ops/GetContracts', { headers: authHeader() }).then(response => {
        let allData = response.data;
        let uniqueData = [];
        let uniqueDataObj = {};
        const whPattern = /WH\d+/i;

        for (let i = 0; i < allData.length; i++) {
          let sp = allData[i];
          if (sp.wH_NumberName) {
            const hasWHNumber = whPattern.test(sp.wH_NumberName);
            const displayName = hasWHNumber ? sp.wH_NumberName : `${sp.contractUniqueID} - ${sp.wH_NumberName}`;

            const spWithDisplayName = { ...sp, displayName };

            uniqueData.push(spWithDisplayName);
            uniqueDataObj[displayName] = spWithDisplayName; // <-- key by displayName now
          }
        }

        setSPList(uniqueData);
        setSPListObj(uniqueDataObj);
        dispatch({ type: FETCH_SUCCESS });
      });
    } catch (error) {
      setSPList([]);
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const getMachineList = async () => {
    try {
      const response = await customAxios.post('/GenTasks/GetDropdownListArray', ['HarvesterMachine', 'ExtractionMachine'], {
        headers: authHeader(),
      });
      if (response.status === 200) {
        setMachineList(response.data);
      } else {
        setMachineList([]);
        dispatch({ type: FETCH_ERROR, payload: 'Failed to load machine list' });
      }
    } catch (error) {
      setMachineList([]);
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const resetAllData = () => {
    const now = toLocalISO(new Date());
    setSelectedTaskType(null);
    setClockInData({ ...DEFAULT_CLOCK_IN_DATA, date_Time: now });
    setVehicalChecklist(DEFAULT_VEHICAL_CHECK_LIST);
    setHarvestingMaintenanceCheckList(DEFAULT_HARVESTING_MAINTENANCE_CHECK_LIST);
    setHarvestingClosingData({ ...DEFAULT_HARVESTING_CLOSING_DATA, date_Time: now });
    setProductionData([]);
    setForwardingData({ ...FORWARDING_DEFAULT_DATA, date_Time: now });
    setForwardingMaintenanceCheckListData(FORWARDING_MAINTENANCE_CHECK_LIST);
    setDailyOtherWorkCloseData({ date_Time: now });
    setCompletedForestData({ ...DEFAULT_COMPLETED_FOREST_DATA, date_Time: now });
    setStockTakeData({ ...DEFAULT_STOCK_TAKE_DATA, date_Time: now });
    setExpenseClaimData(DEFAULT_EXPENSE_CLAIM_DATA);
    setOtherTaskData({ ...DEFAULT_OTHER_WORK_DATA, date_Time: now });
    setHolidayData({ ...DEFAULT_HOLIDAY_DATA, date_Time: now });
    setFiles([]);
    setNewUploadedFiles([]);
  };

  // Called by the type dropdown; re-zeroes just the relevant slice
  // in case the user bounced between task types before submitting.
  const handleSelectTaskType = newType => {
    setSelectedTaskType(newType);
  };

  const handleClose = () => {
    setOpen(false);
    resetAllData();
  };

  // ── local file staging handlers (mirrors useDocs' interface) ───────
  const handleFileChange = key => fileOrFiles => {
    const incoming = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    const staged = incoming.map(f => ({
      id: `local_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`,
      documentName: f.name,
      fileObj: f,
    }));
    setFiles(prev => [...prev, ...staged]);
    setNewUploadedFiles(prev => [...prev, ...incoming]);
  };

  const handleDeleteFiles = fileDataToDelete => {
    setFiles(prev => prev.filter(f => f.id !== fileDataToDelete.id));
    setNewUploadedFiles(prev => prev.filter(f => f !== fileDataToDelete.fileObj));
  };

  const viewFile = fileData => {
    if (!fileData?.fileObj) return;
    const url = URL.createObjectURL(fileData.fileObj);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileData.documentName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── submit flow: sequential, task must exist before children ───────
  const submitNewTask = async () => {
    if (!selectedTaskType) {
      showToast('Please select a task type', 'error');
      return;
    }

    let newTaskData = { selectTask: selectedTaskType };
    let isMaintanenceCheckListEdit = false;
    let maintenanceCheckListName = '';
    let isProductionDataEdit = false;
    let isVehicalCheckListEdit = false;
    let isfilesEdit = false;

    if (selectedTaskType === 'Clock In') {
      newTaskData = {
        ...newTaskData,
        ...clockInData,
        haveCompletedVanDriverWalkAroundCheckSheet: clockInData.haveCompletedVanDriverWalkAroundCheckSheet ? '1' : '0',
      };
      isVehicalCheckListEdit = true;
    } else if (selectedTaskType === 'Daily Harvesting Close') {
      newTaskData = {
        ...newTaskData,
        ...harvestingClosingData,
        subsistenceTracking: harvestingClosingData.subsistenceTracking ? '1' : '0',
        movedLocationForSignal: harvestingClosingData.movedLocationForSignal ? '1' : '0',
      };
      isMaintanenceCheckListEdit = true;
      maintenanceCheckListName = 'harvesting';
      isProductionDataEdit = true;
      isfilesEdit = true;
    } else if (selectedTaskType === 'Daily Forwarding Close') {
      newTaskData = {
        ...newTaskData,
        ...forwardingData,
        subsistenceTracking: forwardingData.subsistenceTracking ? '1' : '0',
        movedLocationForSignal: forwardingData.movedLocationForSignal ? '1' : '0',
      };
      isMaintanenceCheckListEdit = true;
      maintenanceCheckListName = 'forwarding';
      isProductionDataEdit = true;
      isfilesEdit = true;
    } else if (selectedTaskType === 'Daily Other Work Close') {
      newTaskData = {
        ...newTaskData,
        ...dailyOtherWorkCloseData,
        subsistenceTracking: dailyOtherWorkCloseData.subsistenceTracking ? '1' : '0',
      };
      isMaintanenceCheckListEdit = true;
      maintenanceCheckListName = 'both';
    } else if (selectedTaskType === 'Completed Forest') {
      newTaskData = { ...newTaskData, ...completedForestData };
      isProductionDataEdit = true;
      isfilesEdit = true;
    } else if (selectedTaskType === 'Stock Take') {
      newTaskData = { ...newTaskData, ...stockTakeData };
      isProductionDataEdit = true;
    } else if (selectedTaskType === 'Expense Claim') {
      newTaskData = { ...newTaskData, ...expenseClaimData };
      isfilesEdit = true;
    } else if (selectedTaskType === 'Other') {
      newTaskData = { ...newTaskData, ...otherTaskData };
      isfilesEdit = true;
    } else if (selectedTaskType === 'Holidays') {
      newTaskData = { ...newTaskData, ...holidayData };
    } else if (selectedTaskType === 'Help Videos') {
      // no data needed
      handleClose();
      return;
    }

    const createdTask = await handleCreateTask(newTaskData);
    if (!createdTask) return;

    if (isMaintanenceCheckListEdit) {
      const ok = await handleMaintenanceCheckListCreate(maintenanceCheckListName, createdTask.id);
      if (!ok) return;
    }
    if (isVehicalCheckListEdit) {
      const ok = await createVehicalCheckList(createdTask.id);
      if (!ok) return;
    }
    console.log('isProductionDataEdit', isProductionDataEdit, 'productionData', productionData);
    if (isProductionDataEdit && productionData.length > 0) {
      const dataWithTaskId = productionData.map(p => ({ ...p, taskId: createdTask.id }));
      console.log('dataWithTaskId', dataWithTaskId);
      await addNewproductionData(dataWithTaskId);
    }
    if (isfilesEdit) {
      await uploadNewDocs(createdTask);
    }

    handleSuccessfulSubmit();
    handleClose();
  };

  const handleCreateTask = async newTaskData => {
    return new Promise(res => {
      let url = '/Ops/AddTasks';
      dispatch({ type: FETCH_START });
      newTaskData['dateTime'] = newTaskData.date_Time;
      newTaskData['whDocketNo'] = newTaskData.wH_DocketNo;

      customAxios
        .post(url, newTaskData, { headers: authHeader() })
        .then(response => {
          dispatch({ type: FETCH_SUCCESS });

          if (response.status === 200 && response.data?.newOpsTaskId != null) {
            res({ ...response.data, id: response.data.newOpsTaskId }); // normalize to `id` for downstream use
          } else {
            console.error('AddTasks: missing newOpsTaskId in response', response.data);
            showToast('Task created but could not link related records', 'error');
            res(null);
          }
        })
        .catch(error => {
          res(null);
          dispatch({ type: FETCH_ERROR, payload: 'Error Creating Task' });
        });
    });
  };

  const handleMaintenanceCheckListCreate = (typeOfCheckList, taskId) => {
    let url = '/Ops/AddTasksMachineChecklist';
    let maintenanceCheckListData = {};

    if (typeOfCheckList === 'harvesting') {
      maintenanceCheckListData = { ...harvestingMaintenanceCheckList, taskId };
    } else if (typeOfCheckList === 'forwarding') {
      maintenanceCheckListData = { ...forwardingMaintenanceCheckListData, taskId };
    } else {
      maintenanceCheckListData = {
        ...harvestingMaintenanceCheckList,
        forwardingMaintenanceCheckEngineOilLevel:
          forwardingMaintenanceCheckListData.forwardingMaintenanceCheckEngineOilLevel,
        forwardingMaintenanceFillDiesel: forwardingMaintenanceCheckListData.forwardingMaintenanceFillDiesel,
        forwardingMaintenanceGreaseBaseUnit: forwardingMaintenanceCheckListData.forwardingMaintenanceGreaseBaseUnit,
        forwardingMaintenanceGreaseCrane: forwardingMaintenanceCheckListData.forwardingMaintenanceGreaseCrane,
        forwardingMaintenanceGreaseGrab: forwardingMaintenanceCheckListData.forwardingMaintenanceGreaseGrab,
        taskId,
      };
    }

    dispatch({ type: FETCH_START });
    return new Promise(res => {
      customAxios
        .post(url, maintenanceCheckListData, { headers: authHeader() })
        .then(response => {
          dispatch({ type: FETCH_SUCCESS });
          res(response.status === 200);
        })
        .catch(error => {
          res(false);
          dispatch({ type: FETCH_ERROR, payload: 'Error Creating Checklist' });
        });
    });
  };

  const createVehicalCheckList = taskId => {
    let url = '/Ops/AddTasksVehicleChecklist';
    return new Promise(res => {
      dispatch({ type: FETCH_START });
      customAxios
        .post(url, { ...vehicalCheckList, taskId }, { headers: authHeader() })
        .then(response => {
          dispatch({ type: FETCH_SUCCESS });
          res(response.status === 200);
        })
        .catch(error => {
          res(false);
          dispatch({ type: FETCH_ERROR, payload: 'Error Creating Vehicle Checklist' });
        });
    });
  };

  const addNewproductionData = async newProductionData => {
    let url = '/Ops/AddTasksProductionRecord';
    return new Promise(res => {
      dispatch({ type: FETCH_START });
      customAxios
        .post(url, newProductionData, { headers: authHeader() })
        .then(response => {
          dispatch({ type: FETCH_SUCCESS });
          res(response.status === 200);
        })
        .catch(error => {
          res(false);
          dispatch({ type: FETCH_ERROR, payload: 'Error Adding Production Records' });
        });
    });
  };

  const uploadNewDocs = async createdTask => {
    if (!newUploadedFiles || newUploadedFiles.length === 0) return true;

    dispatch({ type: FETCH_START });
    try {
      // Match useDocs' proven convention: one file per request, key 'files', docType 'files'
      for (let i = 0; i < newUploadedFiles.length; i++) {
        const formData = new FormData();
        formData.append('taskId', createdTask.id);
        formData.append('files', newUploadedFiles[i]);
        formData.append('docType', 'files');

        const response = await customFormAxios.post('/Ops/AddTasksDocs', formData, {
          headers: authHeader(),
        });
        if (response.status !== 200) {
          dispatch({ type: FETCH_ERROR, payload: 'Error Uploading document' });
          return false;
        }
      }
      dispatch({ type: FETCH_SUCCESS });
      return true;
    } catch (error) {
      dispatch({ type: FETCH_ERROR, payload: 'Error Uploading document' });
      return false;
    }
  };

  return {
    spList,
    spListObj,
    selectedTaskType,
    handleSelectTaskType,
    handleClose,

    clockInData,
    setClockInData,
    vehicalCheckList,
    setVehicalChecklist,

    harvestingMaintenanceCheckList,
    setHarvestingMaintenanceCheckList,
    harvestingClosingData,
    setHarvestingClosingData,
    productionData,
    setProductionData,

    forwardingData,
    setForwardingData,
    forwardingMaintenanceCheckListData,
    setForwardingMaintenanceCheckListData,

    dailyOtherWorkCloseData,
    setDailyOtherWorkCloseData,

    completedForestData,
    setCompletedForestData,

    stockTakeData,
    setStockTakeData,
    expenseClaimData,
    setExpenseClaimData,
    otherTaskData,
    setOtherTaskData,
    holidayData,
    setHolidayData,

    files,
    setNewUploadedFiles,
    handleFileChange,
    handleDeleteFiles,
    viewFile,
    isLoading: false,

    submitNewTask,

    machineList,
  };
};

export default useHFOpsFormAddHook;
